import { useCallback } from 'react';
import { AxiosRequestConfig } from 'axios';

import {
  useApiGet,
  useApiPatch,
  useApiPost,
  ApiResult,
  FetchApiCallback
} from '../../common/api/Api.hooks';
import { GetAuthUrlDto } from './Jira.dto';

export const convertJiraStatus = (status: string): string => {
  if (status) {
    return status.toLowerCase().replace(' ', '-');
  }
  return status;
};

export function useJiraAuthUrl(): [ApiResult<GetAuthUrlDto>, FetchApiCallback] {
  const { get, ...result } = useApiGet<GetAuthUrlDto>('jira/url');
  return [result, get];
}

export function useSaveJiraCode(): [
  ApiResult<boolean>,
  (code: string) => void
] {
  const { patch: apiPatch, ...result } = useApiPatch<boolean>('jira/code');
  const patch = useCallback((code: string) => apiPatch({ code }), [apiPatch]);
  return [result, patch];
}

export function useCheckJiraAuth(): [ApiResult<boolean>, FetchApiCallback] {
  const { get, ...result } = useApiGet<boolean>('jira/auth');
  return [result, get];
}

// avoid using directly, create separate hooks
export function useJiraProxy<ResponseData>(): [
  ApiResult<ResponseData>,
  (requestConfig: AxiosRequestConfig) => void
] {
  const { post: apiPost, ...result } = useApiPost<ResponseData>('jira/proxy');
  const post = useCallback(
    (requestConfig: AxiosRequestConfig) => apiPost(requestConfig),
    [apiPost]
  );
  return [result, post];
}

export function useJiraUserDetails(): [
  ApiResult<any>,
  (accountId: string) => void
] {
  const [result, proxyPost] = useJiraProxy<any>();
  const post = useCallback(
    (accountId: string) =>
      proxyPost({
        url: 'user',
        params: { accountId }
      }),
    [proxyPost]
  );
  return [result, post];
}

export function useGetJiraIssues(
  projectKey?: string
): [
  ApiResult<any>,
  (
    limit?: number,
    page?: number,
    sort?: string,
    direction?: string,
    search?: string
  ) => void
] {
  const [result, post] = useJiraProxy<any>();
  const getIssues = useCallback(
    (
      limit = 100,
      page = 0,
      sort = 'key',
      direction = 'ASC',
      search?: string
    ) => {
      search = search ? search.replace(/"/g, '') : undefined;
      let jql = projectKey ? `project = "${projectKey}" AND ` : '';
      // jql += `issuetype != Epic`;
      jql += `${
        search ? `AND (id="${search}" OR text ~ "${search}")` : ''
      } ORDER BY ${sort} ${direction}`;
      return post({
        method: 'POST',
        url: '/rest/api/2/search',
        data: {
          jql,
          startAt: page * limit || 0,
          maxResults: limit
        }
      });
    },
    [post, projectKey]
  );
  return [result, getIssues];
}

export function useGetJiraIssue(
  issueIdOrKey: string
): [ApiResult<any>, () => void] {
  const [result, post] = useJiraProxy<any>();
  const getIssue = useCallback(() => {
    return post({
      method: 'GET',
      url: '/rest/api/2/issue/' + issueIdOrKey,
      params: {}
    });
  }, [issueIdOrKey, post]);
  return [result, getIssue];
}

export function useGetIssueEditMeta(
  issueIdOrKey: string
): [ApiResult<any>, () => void] {
  const [editMeta, post] = useJiraProxy<any>();
  const getEditMeta = useCallback(() => {
    return post({
      method: 'GET',
      url: '/rest/api/2/issue/' + issueIdOrKey + '/editmeta'
    });
  }, [issueIdOrKey, post]);
  return [editMeta, getEditMeta];
}

export function useGetIssueTransition(
  issueIdOrKey: string
): [ApiResult<any>, () => void] {
  const [transition, post] = useJiraProxy<any>();
  const getIssueTransition = useCallback(() => {
    return post({
      method: 'GET',
      url: '/rest/api/2/issue/' + issueIdOrKey + '/transitions'
    });
  }, [issueIdOrKey, post]);
  return [transition, getIssueTransition];
}

export function usePostIssueTransition(
  issueIdOrKey: string
): [ApiResult<any>, (issue: any) => void] {
  const [status, post] = useJiraProxy<any>();
  const postIssueTransition = useCallback(
    (data: any) => {
      return post({
        method: 'POST',
        url: '/rest/api/2/issue/' + issueIdOrKey + '/transitions',
        data: data
      });
    },
    [issueIdOrKey, post]
  );
  return [status, postIssueTransition];
}
